/**
 * Modulbelegung (Kapitel F).
 *
 * Gerechnet wird ausschliesslich in der Dachebene. Wer das Raster im Grundriss
 * legt, bekommt zu wenige Module aufs Dach — siehe dachebene.ts.
 *
 * Ablauf:
 *   1. Randabstaende abziehen -> nutzbare Flaeche (Traufe, First, Ortgang)
 *   2. Das Raster so in die Flaeche schieben, dass die meisten Module passen,
 *      und unter allen gleich guten Lagen die gewuenschte nehmen: an der Traufe,
 *      mittig oder am First, seitlich links, mittig oder rechts
 *   3. Modul nur setzen, wenn es vollstaendig innen und ausserhalb aller
 *      Aussparungen liegt (PV-F04: halbe Module gibt es nicht)
 *   4. Zum Schluss den Versatz von Hand dazu, wahlweise gedreht
 */
import { gradInBogen } from './typen.js';
import { beschneideAmKasten, dreheRing, flaecheninhalt, huellkasten, punktInnerhalb, rechteck, schwerpunkt, ueberlappen, vollstaendigInnerhalb, weiteAuf, } from './polygon.js';
import { reihengeometrie } from './flachdach.js';
/** Seitlich mittig, an der Traufe — so wird am Steildach ueblicherweise montiert. */
export const STANDARD_FELDLAGE = { quer: 'mitte', laengs: 'traufe' };
/**
 * In einem eigens gezeichneten Modulfeld sitzen die Module mittig — wer ein
 * Feld zeichnet, hat die Lage schon gewaehlt (Mathias, 11/2026).
 */
export const MITTIGE_FELDLAGE = { quer: 'mitte', laengs: 'mitte' };
export const DEFAULT_REGELN = {
    ausrichtung: 'hoch',
    randTraufeMm: 300,
    randFirstMm: 300,
    randOrtgangMm: 300,
    fugeQuerMm: 20,
    fugeLaengsMm: 20,
    sicherheitsabstandMm: 300,
};
/** Modulmasse in der Dachebene, abhaengig von hoch oder quer. */
export function modulInEbene(modul, regeln) {
    const breiteM = (regeln.ausrichtung === 'hoch' ? modul.breiteMm : modul.hoeheMm) / 1000;
    const laengsM = (regeln.ausrichtung === 'hoch' ? modul.hoeheMm : modul.breiteMm) / 1000;
    // Aufgestaendert belegt das Modul nur seine waagrechte Projektion.
    const hoeheM = regeln.aufstaenderung
        ? reihengeometrie(laengsM, regeln.aufstaenderung).modultiefeM
        : laengsM;
    return { breiteM, hoeheM };
}
function achse(schritt, fugeM, nach, abstandMm) {
    const n = nach != null ? Math.floor(nach) : 0;
    const zuschlag = abstandMm != null ? abstandMm / 1000 - fugeM : 0;
    if (n < 1 || !(zuschlag > 1e-6))
        return { schritt, gruppe: Infinity, zuschlag: 0, periode: schritt };
    return { schritt, gruppe: n, zuschlag, periode: n * schritt + zuschlag };
}
/** Lage des k-ten Moduls auf der Achse, vom Start aus. */
const lageAuf = (a, k) => k * a.schritt + (a.gruppe === Infinity ? 0 : Math.floor(k / a.gruppe) * a.zuschlag);
/**
 * Wie weit eine Lage von der gewuenschten abweicht, je Achse, als
 * Verschiebung in Metern: so weit muesste das Feld noch wandern.
 *
 * Buendig (Traufe, First, links, rechts) heisst: bis an den Mindestabstand,
 * also gemessen gegen die nutzbare Flaeche. Mittig heisst: mittig auf dem
 * Dach, also gemessen gegen das ganze Dach — sonst saesse das Feld bei 30 cm
 * Traufabstand und 40 cm Firstabstand um 5 cm daneben. Die Mindestabstaende
 * haelt die Suche dabei ohnehin ein, weil sie nur Plaetze in der nutzbaren
 * Flaeche zaehlt.
 */
function lagefehler(raster, nutz, dach, feldlage) {
    if (raster.plaetze.length === 0)
        return { laengs: Infinity, quer: Infinity, unwucht: Infinity };
    const block = huellkasten(raster.plaetze.flatMap((p) => p.ecken));
    // Wie weit der Schwerpunkt der Module neben der Mitte des Blocks liegt. Bei
    // gleicher Zahl und gleicher Lage entscheidet das zwischen 1 | 3 | 3 und
    // 2 | 3 | 2 — mit Trennabstand sieht nur das zweite mittig aus.
    let sx = 0;
    let sy = 0;
    for (const p of raster.plaetze) {
        const k = huellkasten(p.ecken);
        sx += (k.minX + k.maxX) / 2;
        sy += (k.minY + k.maxY) / 2;
    }
    const n = raster.plaetze.length;
    const unwucht = Math.abs(sx / n - (block.minX + block.maxX) / 2) + Math.abs(sy / n - (block.minY + block.maxY) / 2);
    return {
        unwucht,
        laengs: feldlage.laengs === 'traufe'
            ? nutz.minY - block.minY
            : feldlage.laengs === 'first'
                ? nutz.maxY - block.maxY
                : (dach.maxY - block.maxY - (block.minY - dach.minY)) / 2,
        quer: feldlage.quer === 'links'
            ? nutz.minX - block.minX
            : feldlage.quer === 'rechts'
                ? nutz.maxX - block.maxX
                : (dach.maxX - block.maxX - (block.minX - dach.minX)) / 2,
    };
}
/**
 * Die Lage des Rasters suchen: erst die meisten Module, dann die gewuenschte
 * Ausrichtung.
 *
 * Vorher war das Raster starr an der unteren linken Ecke des Huellrechtecks
 * festgemacht. Bei einer von Hand gezeichneten, leicht schiefen Traufe passte
 * die erste Reihe an der tiefsten Stelle nicht hinein, und das ganze Feld
 * sprang um eine Modulhoehe nach oben — Traufabstand 0 eingestellt, 1,78 m
 * herausgekommen (Rueckmeldung aus dem ersten Test bei Wania, 10/2026).
 *
 * Ein Raster wiederholt sich nach einem Schritt, also genuegt es, die Lage
 * innerhalb eines Schritts je Achse zu suchen: erst grob in der Flaeche, dann
 * auf zwei Zentimeter genau je Achse. Das sind rund 200 Lagen statt Tausender
 * und reicht, weil auf dem Dach ohnehin niemand genauer als auf ein paar
 * Zentimeter montiert.
 */
function waehleLage(lege, periodeU, periodeV, nutz, dach, feldlage, gruppiert = false) {
    const bewerte = (du, dv) => {
        const raster = lege(du, dv);
        return { du, dv, anzahl: raster.plaetze.length, ...lagefehler(raster, nutz, dach, feldlage) };
    };
    // Mit Trennung zaehlt nach der Zahl zuerst, wie ausgewogen geteilt ist —
    // 2 | 3 | 2 vor 1 | 3 | 3. Die Unwucht haengt nur davon ab, welche Plaetze
    // belegt sind, nicht vom letzten Zentimeter der Lage; stuende sie hinter der
    // Lage, entschiede die Schrittweite der Suche.
    const besser = (a, b) => a.anzahl !== b.anzahl
        ? a.anzahl > b.anzahl
        : gruppiert && Math.abs(a.unwucht - b.unwucht) > 0.01
            ? a.unwucht < b.unwucht
            : Math.abs(Math.abs(a.laengs) - Math.abs(b.laengs)) > 1e-6
                ? Math.abs(a.laengs) < Math.abs(b.laengs)
                : Math.abs(a.quer) < Math.abs(b.quer) - 1e-6;
    const GROB = 8;
    const FEIN_M = 0.02;
    let bester = bewerte(0, 0);
    for (let i = 0; i < GROB; i++) {
        for (let j = 0; j < GROB; j++) {
            const k = bewerte((i * periodeU) / GROB, (j * periodeV) / GROB);
            if (besser(k, bester))
                bester = k;
        }
    }
    if (bester.anzahl === 0)
        return { du: 0, dv: 0 };
    // Fein, Achse fuer Achse. Laengs zweimal, weil sich mit der Querlage die
    // Reihen aendern koennen, die an einer schiefen Kante gerade noch passen.
    // Mit Trennabstand ist die Periode eine ganze Gruppe, zehn Module sind elf
    // Meter: dann erst auf 150 Lagen, und nur um die beste herum auf 2 cm.
    const HOECHSTENS = 150;
    const fein = (achse) => {
        const periode = achse === 'u' ? periodeU : periodeV;
        const pruefe = (d) => {
            const k = achse === 'u' ? bewerte(d, bester.dv) : bewerte(bester.du, d);
            if (besser(k, bester))
                bester = k;
        };
        if (periode / FEIN_M <= HOECHSTENS) {
            for (let d = 0; d < periode; d += FEIN_M)
                pruefe(d);
            return;
        }
        const grob = periode / HOECHSTENS;
        for (let d = 0; d < periode; d += grob)
            pruefe(d);
        const mitte = achse === 'u' ? bester.du : bester.dv;
        for (let d = mitte - grob; d <= mitte + grob; d += FEIN_M)
            pruefe(d);
    };
    fein('v');
    fein('u');
    fein('v');
    // Zum Schluss genau hinschieben: Die Suche trifft nur auf zwei Zentimeter,
    // der verbleibende Fehler ist aber bekannt. Geht dabei ein Modul verloren —
    // an einer schiefen Kante moeglich —, nur halb so weit, und so fort.
    const schiebe = (achse) => {
        let weg = achse === 'u' ? bester.quer : bester.laengs;
        for (let versuch = 0; versuch < 6 && Math.abs(weg) > 1e-4; versuch++) {
            const k = achse === 'u' ? bewerte(bester.du + weg, bester.dv) : bewerte(bester.du, bester.dv + weg);
            if (k.anzahl >= bester.anzahl) {
                bester = k;
                return;
            }
            weg /= 2;
        }
    };
    schiebe('v');
    schiebe('u');
    return { du: bester.du, dv: bester.dv };
}
/**
 * Raster legen.
 *
 * @param dachflaeche   Polygon in der Dachebene
 * @param aussparungen  Kamine, Dachfenster, Gauben — ebenfalls in der Dachebene
 * @param modulfeld     Ein eigens gezeichnetes Feld auf dem Dach (Mathias,
 *                      11/2026). Belegt wird dann nur darin, mittig, und die
 *                      Randabstaende zum Dach gelten weiter.
 */
export function belege(dachflaeche, aussparungen, modul, regeln, modulfeld) {
    if (dachflaeche.length < 3)
        return { module: [], nutzflaeche: [], reihen: 0, spalten: 0 };
    const kasten = huellkasten(dachflaeche);
    const nutzflaeche = beschneideAmKasten(dachflaeche, {
        minX: kasten.minX + regeln.randOrtgangMm / 1000,
        maxX: kasten.maxX - regeln.randOrtgangMm / 1000,
        minY: kasten.minY + regeln.randTraufeMm / 1000,
        maxY: kasten.maxY - regeln.randFirstMm / 1000,
    });
    if (nutzflaeche.length < 3)
        return { module: [], nutzflaeche: [], reihen: 0, spalten: 0 };
    const { breiteM, hoeheM } = modulInEbene(modul, regeln);
    if (breiteM <= 0 || hoeheM <= 0)
        throw new Error('belege: Modulmaße müssen positiv sein');
    const laengsM = (regeln.ausrichtung === 'hoch' ? modul.hoeheMm : modul.breiteMm) / 1000;
    const reihenabstandM = regeln.aufstaenderung
        ? reihengeometrie(laengsM, regeln.aufstaenderung).reihenabstandM
        : undefined;
    const schrittU = breiteM + regeln.fugeQuerMm / 1000;
    const schrittV = reihenabstandM ?? hoeheM + regeln.fugeLaengsMm / 1000;
    const t = regeln.trennung;
    const achseU = achse(schrittU, regeln.fugeQuerMm / 1000, t?.querNach, t?.querAbstandMm);
    // Am Flachdach ist der Reihenabstand die Fuge: Die Trennung muss darueber liegen.
    const achseV = achse(schrittV, schrittV - hoeheM, t?.laengsNach, t?.laengsAbstandMm);
    // Alles in den Rasterrahmen drehen, dort achsparallel arbeiten, am Ende
    // zurueckdrehen. Sonst wird jede Eckenpruefung eine Sonderbehandlung.
    const drehung = gradInBogen(regeln.drehungGrad ?? 0);
    const zentrum = schwerpunkt(nutzflaeche);
    const nutzGedreht = drehung === 0 ? nutzflaeche : dreheRing(nutzflaeche, -drehung, zentrum);
    const sicherheit = (regeln.sicherheitsabstandMm ?? 0) / 1000;
    const sperrflaechen = aussparungen
        .filter((a) => a.length >= 3)
        .map((a) => weiteAuf(a, sicherheit))
        .map((a) => (drehung === 0 ? a : dreheRing(a, -drehung, zentrum)));
    const feldGedreht = modulfeld && modulfeld.length >= 3
        ? drehung === 0
            ? modulfeld
            : dreheRing(modulfeld, -drehung, zentrum)
        : null;
    const nutzKasten = huellkasten(nutzGedreht);
    const feldKasten = feldGedreht ? huellkasten(feldGedreht) : null;
    const rahmen = feldKasten
        ? {
            minX: Math.max(nutzKasten.minX, feldKasten.minX),
            maxX: Math.min(nutzKasten.maxX, feldKasten.maxX),
            minY: Math.max(nutzKasten.minY, feldKasten.minY),
            maxY: Math.min(nutzKasten.maxY, feldKasten.maxY),
        }
        : nutzKasten;
    if (rahmen.maxX <= rahmen.minX || rahmen.maxY <= rahmen.minY) {
        return { module: [], nutzflaeche, reihen: 0, spalten: 0 };
    }
    // Mit Trennung beginnt das Raster eine Gruppe vor dem Rahmen: Sonst saesse
    // am Rand immer eine volle Gruppe, und mittig liesse sich ein Feld mit
    // 4 | 5 | 4 Modulen nicht legen.
    const vorlaufU = achseU.gruppe === Infinity ? 0 : achseU.periode;
    const vorlaufV = achseV.gruppe === Infinity ? 0 : achseV.periode;
    /** Das Raster ab einem Startpunkt legen; zurueck kommen die Plaetze im gedrehten Rahmen. */
    const lege = (du, dv) => {
        const startU = rahmen.minX + du - vorlaufU;
        const startV = rahmen.minY + dv - vorlaufV;
        const spalten = Math.max(0, Math.floor((rahmen.maxX - startU + 1e-9) / schrittU) + 1);
        const reihen = Math.max(0, Math.floor((rahmen.maxY - startV + 1e-9) / schrittV) + 1);
        const plaetze = [];
        for (let reihe = 0; reihe < reihen; reihe++) {
            const v = startV + lageAuf(achseV, reihe);
            if (v < rahmen.minY - 1e-9 || v + hoeheM > rahmen.maxY + 1e-9)
                continue;
            for (let spalte = 0; spalte < spalten; spalte++) {
                const u = startU + lageAuf(achseU, spalte);
                if (u < rahmen.minX - 1e-9 || u + breiteM > rahmen.maxX + 1e-9)
                    continue;
                const platz = rechteck(u, v, breiteM, hoeheM);
                if (!vollstaendigInnerhalb(platz, nutzGedreht))
                    continue;
                if (feldGedreht && !vollstaendigInnerhalb(platz, feldGedreht))
                    continue;
                if (sperrflaechen.some((sperre) => ueberlappen(platz, sperre)))
                    continue;
                plaetze.push({ reihe, spalte, ecken: platz });
            }
        }
        return { plaetze, reihen, spalten };
    };
    // "Mittig" misst gegen das, worin belegt wird: das ganze Dach oder das Feld,
    // soweit es auf dem Dach liegt.
    const dachGedreht = drehung === 0 ? dachflaeche : dreheRing(dachflaeche, -drehung, zentrum);
    const dachKasten = huellkasten(dachGedreht);
    const lage = waehleLage(lege, achseU.periode, achseV.periode, rahmen, feldKasten
        ? {
            minX: Math.max(dachKasten.minX, feldKasten.minX),
            maxX: Math.min(dachKasten.maxX, feldKasten.maxX),
            minY: Math.max(dachKasten.minY, feldKasten.minY),
            maxY: Math.min(dachKasten.maxY, feldKasten.maxY),
        }
        : dachKasten, regeln.feldlage ?? (feldGedreht ? MITTIGE_FELDLAGE : STANDARD_FELDLAGE), achseU.gruppe !== Infinity || achseV.gruppe !== Infinity);
    const { plaetze, reihen, spalten } = lege(lage.du + (regeln.versatzU ?? 0), lage.dv + (regeln.versatzV ?? 0));
    const module = plaetze.map(({ reihe, spalte, ecken: platz }) => {
        const ecken = drehung === 0 ? platz : dreheRing(platz, drehung, zentrum);
        return { reihe, spalte, mitte: schwerpunkt(ecken), ecken };
    });
    return { module, nutzflaeche, reihen, spalten, ...(reihenabstandM ? { reihenabstandM } : {}) };
}
/** Laufende Anzeige beim Planen (PV-F08). */
export function kennzahlen(belegung, modul, wahreFlaecheM2) {
    const anzahl = belegung.module.length;
    const flaecheJeModul = (modul.breiteMm / 1000) * (modul.hoeheMm / 1000);
    const belegteFlaecheM2 = anzahl * flaecheJeModul;
    return {
        anzahl,
        leistungKwp: (anzahl * modul.nennleistungWp) / 1000,
        belegteFlaecheM2,
        belegungsgrad: wahreFlaecheM2 > 0 ? belegteFlaecheM2 / wahreFlaecheM2 : 0,
    };
}
export function abmessungen(dachflaeche, belegung) {
    if (dachflaeche.length < 3) {
        return {
            huellgenauigkeit: 0,
            dachBreiteM: 0,
            dachLaengeM: 0,
            belegtBreiteM: null,
            belegtLaengeM: null,
            randLinksM: null,
            randRechtsM: null,
            randTraufeM: null,
            randFirstM: null,
        };
    }
    const dach = huellkasten(dachflaeche);
    const kastenflaeche = (dach.maxX - dach.minX) * (dach.maxY - dach.minY);
    const huellgenauigkeit = kastenflaeche > 0 ? Math.min(1, Math.abs(flaecheninhalt(dachflaeche)) / kastenflaeche) : 0;
    const leer = {
        huellgenauigkeit,
        dachBreiteM: dach.maxX - dach.minX,
        dachLaengeM: dach.maxY - dach.minY,
        belegtBreiteM: null,
        belegtLaengeM: null,
        randLinksM: null,
        randRechtsM: null,
        randTraufeM: null,
        randFirstM: null,
    };
    if (belegung.module.length === 0)
        return leer;
    // Der Block ist das Huellmass ueber alle Modulecken — bei gedrehtem Raster
    // ist das mehr als Spaltenzahl mal Modulbreite, und genau das will man sehen.
    const alleEcken = belegung.module.flatMap((m) => m.ecken);
    const block = huellkasten(alleEcken);
    return {
        huellgenauigkeit,
        dachBreiteM: dach.maxX - dach.minX,
        dachLaengeM: dach.maxY - dach.minY,
        belegtBreiteM: block.maxX - block.minX,
        belegtLaengeM: block.maxY - block.minY,
        randLinksM: block.minX - dach.minX,
        randRechtsM: dach.maxX - block.maxX,
        randTraufeM: block.minY - dach.minY,
        randFirstM: dach.maxY - block.maxY,
    };
}
export function reserve(masse, modul, regeln) {
    if (masse.belegtBreiteM === null || masse.belegtLaengeM === null)
        return null;
    const { breiteM, hoeheM } = modulInEbene(modul, regeln);
    const schrittV = regeln.aufstaenderung
        ? reihengeometrie((regeln.ausrichtung === 'hoch' ? modul.hoeheMm : modul.breiteMm) / 1000, regeln.aufstaenderung).reihenabstandM
        : hoeheM + regeln.fugeLaengsMm / 1000;
    return {
        reiheBrauchtM: schrittV,
        reiheFreiM: Math.max(0, masse.dachLaengeM - masse.belegtLaengeM - (regeln.randTraufeMm + regeln.randFirstMm) / 1000),
        spalteBrauchtM: breiteM + regeln.fugeQuerMm / 1000,
        spalteFreiM: Math.max(0, masse.dachBreiteM - masse.belegtBreiteM - (2 * regeln.randOrtgangMm) / 1000),
    };
}
/**
 * Beide Ausrichtungen durchrechnen, damit die guenstigere sofort sichtbar ist
 * (PV-F01).
 */
export function vergleicheAusrichtungen(dachflaeche, aussparungen, modul, regeln, modulfelder) {
    return {
        hoch: belegeFelder(dachflaeche, aussparungen, modul, { ...regeln, ausrichtung: 'hoch' }, modulfelder),
        quer: belegeFelder(dachflaeche, aussparungen, modul, { ...regeln, ausrichtung: 'quer' }, modulfelder),
    };
}
/**
 * Um so viel wird die Reihennummer je weiterem Modulfeld verschoben. Module
 * und Strings hängen an „Reihe:Spalte“ — ohne Versatz trügen das erste Modul
 * in Feld 1 und in Feld 2 dieselbe Kennung. Feld 1 behält seine Nummern,
 * damit Strings und entfernte Plätze aus der Zeit mit einem Feld gültig
 * bleiben. Tausend Reihen hat kein Dach.
 */
export const FELDVERSATZ_REIHEN = 1000;
/**
 * Mehrere Modulfelder auf einer Fläche (Mathias, 11/2026): jedes für sich
 * belegt, mittig in seinem Feld, mit denselben Regeln. Überlappen sich zwei
 * Felder, wird ein Modul, das schon im früheren liegt, nicht noch einmal
 * gesetzt. Ohne Feld wird die ganze Fläche belegt.
 */
export function belegeFelder(dachflaeche, aussparungen, modul, regeln, modulfelder) {
    const felder = (modulfelder ?? []).filter((f) => f.length >= 3);
    if (felder.length === 0)
        return belege(dachflaeche, aussparungen, modul, regeln);
    if (felder.length === 1)
        return belege(dachflaeche, aussparungen, modul, regeln, felder[0]);
    const module = [];
    let reihen = 0;
    let spalten = 0;
    let erstes = null;
    felder.forEach((feld, i) => {
        const b = belege(dachflaeche, aussparungen, modul, regeln, feld);
        erstes ??= b;
        reihen += b.reihen;
        spalten = Math.max(spalten, b.spalten);
        for (const m of b.module) {
            // `ueberlappen` lässt Berührung zu — und damit auch zwei deckungsgleiche
            // Module, deren Ecken alle auf dem Rand des anderen liegen. Deshalb auch
            // die Mitte prüfen.
            if (module.some((schon) => ueberlappen(m.ecken, schon.ecken) || punktInnerhalb(m.mitte, schon.ecken))) {
                continue;
            }
            module.push({ ...m, reihe: m.reihe + i * FELDVERSATZ_REIHEN });
        }
    });
    return {
        module,
        nutzflaeche: erstes.nutzflaeche,
        reihen,
        spalten,
        ...(erstes.reihenabstandM ? { reihenabstandM: erstes.reihenabstandM } : {}),
    };
}
/** Flaecheninhalt der nutzbaren Flaeche — fuer die Anzeige, was der Rand kostet. */
export const nutzflaecheninhalt = (belegung) => belegung.nutzflaeche.length >= 3 ? flaecheninhalt(belegung.nutzflaeche) : 0;
//# sourceMappingURL=raster.js.map